/**
 * theme-customization A3 — the BROWSER side of the theme contract: CSS serialization and
 * OWNED DOM application.
 *
 * `packages/shared` decides WHAT a palette resolves to (`resolveTheme` returns the complete
 * token -> hex map, code-owned partners included). This module decides how that map is written
 * into CSS, and it owns nothing else:
 *
 *   · `themeDeclarations()` turns a resolved map into the exact custom properties the
 *     stylesheets declare. The SHAPE comes from the shared catalogue, never from a name guess:
 *     a `triplet` token (the 19 shadcn channel variables in `globals.css`) is written as a bare
 *     `h s% l%` triplet, and a `hex-pair` token (the `tokens.css` hexes) is written as its hex
 *     PLUS the `-hsl` companion that `tailwind.config.ts` reads. Writing one shape where the
 *     other belongs would leave every consumer of that token painting `unset`.
 *   · `applyOwnedTheme()` writes them onto an element and hands back the exact revert. Only the
 *     properties this module set are touched, so an unrelated inline custom property on the same
 *     element survives both the apply and the revert.
 *
 * The provider is the only caller in A3; A3 task 3's `ReportView` reuses `themeCssText()` for the
 * fixed stock-light report boundary, and the `@media print` block in `globals.css` is the same
 * text (asserted in `app/__tests__/ThemeProvider.test.tsx`, so the copy cannot drift).
 *
 * NO PERSISTENCE LIVES HERE. There is no theme `localStorage` / `sessionStorage` key anywhere in
 * the runtime — the palette is authenticated server state, re-read per session.
 */

import {
  STOCK_COLOURS,
  THEME_TOKEN_IDS,
  THEME_TOKEN_SHAPES,
  type ResolvedTheme,
  type ThemeMode,
  type ThemeTokenId,
} from '@socialsoup/shared';

/**
 * sRGB hex -> the `h s% l%` channel triplet Tailwind reads, ONE DECIMAL per channel.
 *
 * Byte-for-byte the algorithm and rounding convention of `scripts/gen-hsl-companions.cjs`
 * (`round1 = Math.round(x * 10) / 10`), which is what generated every `--*-hsl` companion in
 * `tokens.css`. That script is a CommonJS build-time tool outside the Vite graph, so the
 * function is re-expressed here rather than imported; `app/__tests__/ThemeProvider.test.tsx`
 * pins the two against each other over the whole stock palette, so a divergence in either
 * direction reddens instead of silently repainting every hex-pair token at runtime.
 */
export function hexToHslTriplet(hex: string): string {
  const round1 = (x: number): number => Math.round(x * 10) / 10;
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
    if (h < 0) h += 360;
  }
  return `${round1(h)} ${round1(s * 100)}% ${round1(l * 100)}%`;
}

/** One CSS custom property and the value to declare for it. */
export type ThemeDeclaration = readonly [property: string, value: string];

/**
 * The COMPLETE owned property set for a resolved palette, in catalogue order.
 *
 * "Complete" is the contract, not an optimisation: a partial write leaves some tokens on the
 * stylesheet's stock value and some on the override, which is a palette no one designed and no
 * contrast pair measured.
 *
 * The SIZE of that set is deliberately not written here as a number. It is exactly one property
 * per `triplet` token and two per `hex-pair` token — the hex and its `-hsl` companion — over the
 * whole of `THEME_TOKEN_IDS`, which is the loop immediately below and the only source of truth.
 * (An earlier version of this comment did quote the counts and went stale the moment A2a added
 * its semantic aliases to the catalogue; the totals it named are still sitting in a pre-A2a
 * build.) What matters is not the total but the COVERAGE, and that is asserted rather than
 * described: `styles/__tests__/theme-pair-coverage.test.ts` §12 requires this function's output
 * to contain every editable token AND every editable companion, and proves the check
 * non-vacuous by planting both a truncated set and a companion-less one.
 */
export function themeDeclarations(resolved: ResolvedTheme): ThemeDeclaration[] {
  const out: ThemeDeclaration[] = [];
  for (const token of THEME_TOKEN_IDS as ThemeTokenId[]) {
    const hex = resolved[token];
    if (THEME_TOKEN_SHAPES[token] === 'triplet') {
      out.push([token, hexToHslTriplet(hex)]);
    } else {
      out.push([token, hex]);
      out.push([`${token}-hsl`, hexToHslTriplet(hex)]);
    }
  }
  return out;
}

/** The same declarations as CSS text, one per line, each prefixed with `indent`. */
export function themeCssText(resolved: ResolvedTheme, indent = ''): string {
  return themeDeclarations(resolved)
    .map(([property, value]) => `${indent}${property}: ${value};`)
    .join('\n');
}

// ─── Portal printing ─────────────────────────────────────────────────────────

/** The class `PortalLayout` puts on its root; the print rule below and `globals.css` target it. */
export const PORTAL_PRINT_SURFACE_CLASS = 'portal-print-surface';

/**
 * The COMPLETE stock-light palette, scoped to the portal wrapper under print media.
 *
 * WHY THIS IS SERIALIZED RATHER THAN WRITTEN INTO `globals.css`. The runtime writes its
 * variables as inline custom properties on `documentElement`, and inline properties inherit, so
 * printing a customised (or, from B1, dark) portal would otherwise print those values. The fix
 * is to re-declare the whole set closer to the content — but a hand-copied block in a stylesheet
 * would be a SECOND declaration site for every token, and A0's scope guard
 * (`styles/__tests__/theme-surface-coverage.tokens.test.ts`, "declared exactly once") exists
 * precisely to forbid that: it pins `--bg-card`, `--fixed-white` and `--fixed-dark` at one site
 * each and `--card` at three. Emitting the block from the same serializer the runtime uses keeps
 * the stylesheet's one-site contract intact AND makes drift impossible — there is no second copy
 * of the palette to fall out of date. `globals.css` keeps the part that is not a token
 * declaration: the stock-white sheet and the wrapper's explicit foreground.
 */
export function portalPrintCss(): string {
  return [
    '@media print {',
    `  .${PORTAL_PRINT_SURFACE_CLASS} {`,
    themeCssText(STOCK_COLOURS.light, '    '),
    '  }',
    '}',
  ].join('\n');
}

/**
 * Write the palette onto `element` and return the exact revert.
 *
 * OWNERSHIP IS THE POINT. The revert restores each property to the inline value it had before
 * this call (removing it when there was none) and does the same for `color-scheme`, so:
 *   · an unrelated inline custom property set by something else is never removed;
 *   · a second apply followed by both reverts in reverse order leaves the element exactly as it
 *     started, which is what "clear theme before public paint" has to mean for a shell that can
 *     mount, remount and tear down while a route transition is in flight.
 *
 * `color-scheme` is set so form controls, scrollbars and the UA's own surfaces follow the mode
 * rather than staying on the OS default.
 */
export function applyOwnedTheme(
  element: HTMLElement,
  resolved: ResolvedTheme,
  mode: ThemeMode,
): () => void {
  const declarations = themeDeclarations(resolved);
  const previous: ThemeDeclaration[] = declarations.map(([property]) => [
    property,
    element.style.getPropertyValue(property),
  ]);
  const previousColorScheme = element.style.getPropertyValue('color-scheme');

  for (const [property, value] of declarations) element.style.setProperty(property, value);
  element.style.setProperty('color-scheme', mode);

  let reverted = false;
  return () => {
    // Idempotent: React can run a layout-effect cleanup once, and a provider teardown may also
    // revert explicitly. Restoring twice would re-apply a value the second owner already owns.
    if (reverted) return;
    reverted = true;
    for (const [property, value] of previous) {
      if (value === '') element.style.removeProperty(property);
      else element.style.setProperty(property, value);
    }
    if (previousColorScheme === '') element.style.removeProperty('color-scheme');
    else element.style.setProperty('color-scheme', previousColorScheme);
  };
}
